/**
 * Settings → Notifications (company_admin+; the route is wrapped in
 * <RequireRole>). SMS status, contact hours, recipients (text or Slack, with
 * the Slack connection banner), nudges, assessment auto-send. Edits accumulate
 * in a draft; Save validates, then PUTs. An edit clears its own inline error.
 */
import { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { errorMessage, useToast } from '@/components/Toast';
import { usePageTitle } from '@/lib/page-title';
import { useRoles } from '@/features/pipeline/api';
import { ContactHoursEditor } from './ContactHoursEditor';
import { clearError, type ErrorTouch } from './errors';
import { NudgeEditor } from './NudgeEditor';
import { RecipientsList } from './RecipientsList';
import { useConnections, useSaveSettings, useSettings, useSlackChannels } from './settings-api';
import type { ChannelsState } from './SlackChannelPicker';
import { SmsStatusBanner } from './SmsStatusBanner';
import { hasErrors, validateSettings, type Settings, type SettingsErrors, type SettingsPatch } from './types';

const NO_ERRORS: SettingsErrors = { recipients: {} };

function toPatch(s: Settings): SettingsPatch {
  return { contact_hours: s.contact_hours, recipients: s.recipients, assessment: s.assessment, nudge: s.nudge };
}

export default function NotificationsSettingsPage() {
  usePageTitle('Notifications');
  const settingsQ = useSettings();
  const rolesQ = useRoles();
  const connectionsQ = useConnections();
  const channelsQ = useSlackChannels();
  const save = useSaveSettings();
  const toast = useToast();
  const [draft, setDraft] = useState<SettingsPatch | null>(null);
  const [errors, setErrors] = useState<SettingsErrors>(NO_ERRORS);

  const roles = (rolesQ.data ?? []).filter((r) => r.slug !== 'unmapped');

  if (settingsQ.isPending) {
    return (
      <div className="max-w-[900px] px-4 py-5 sm:px-6 sm:py-6">
        <PageHeader title="Notifications" />
        <div className="space-y-3" aria-busy="true" aria-label="Loading settings">
          {[0, 1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-white/70" />)}
        </div>
      </div>
    );
  }
  if (settingsQ.isError || !settingsQ.data) {
    return (
      <div className="max-w-[900px] px-4 py-5 sm:px-6 sm:py-6">
        <PageHeader title="Notifications" />
        <p role="alert" className="text-sm text-danger">Could not load settings: {errorMessage(settingsQ.error)}</p>
        <button type="button" onClick={() => void settingsQ.refetch()} className="mt-3 inline-flex min-h-10 items-center rounded-md border border-gray-200 bg-white px-3 text-sm font-medium hover:bg-page">Try again</button>
      </div>
    );
  }

  const saved = settingsQ.data;
  const current: SettingsPatch = draft ?? toPatch(saved);
  const dirty = draft !== null;
  const edit = (patch: Partial<SettingsPatch>, touch?: ErrorTouch) => {
    setDraft({ ...current, ...patch });
    if (touch) setErrors((prev) => clearError(prev, touch));
  };
  const channels: ChannelsState = {
    status: channelsQ.isPending ? 'loading' : channelsQ.isError ? 'error' : 'ready',
    channels: channelsQ.data ?? [],
  };

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validateSettings(current);
    setErrors(found);
    if (hasErrors(found)) {
      toast.push({ kind: 'error', title: 'Fix the highlighted fields', message: 'Nothing was saved.' });
      return;
    }
    save.mutate(current, {
      onSuccess: () => {
        setDraft(null);
        toast.push({ kind: 'success', title: 'Notification settings saved' });
      },
      onError: (err) => toast.push({ kind: 'error', title: 'Could not save settings', message: errorMessage(err) }),
    });
  };

  return (
    // `noValidate`: the nudge hours carry min/max for the spinner, but the page's own
    // inline errors (not the browser's bubble) must be what blocks Save.
    <form onSubmit={onSave} noValidate className="max-w-[900px] px-4 py-5 sm:px-6 sm:py-6" aria-label="Notification settings">
      <PageHeader
        title="Notifications"
        subtitle="Who hears about candidates by text or Slack, when messages may go out, and what is sent automatically."
        actions={
          <>
            {dirty && (
              <button type="button" onClick={() => { setDraft(null); setErrors(NO_ERRORS); }} className="inline-flex min-h-10 items-center rounded-md px-3 text-sm font-medium text-gray-700 hover:bg-white">
                Discard
              </button>
            )}
            <button type="submit" disabled={!dirty || save.isPending} className="inline-flex min-h-10 items-center rounded-md bg-navy px-3.5 text-sm font-medium text-white hover:bg-navy-dark disabled:opacity-50">
              {save.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      />
      <div className="space-y-4">
        <SmsStatusBanner sms={saved.sms} />
        <ContactHoursEditor
          value={current.contact_hours}
          errors={errors.contact_hours}
          onChange={(contact_hours, field) => edit({ contact_hours }, field && { section: 'contact_hours', field })}
        />
        <RecipientsList
          recipients={current.recipients}
          roles={roles}
          errors={errors.recipients}
          connections={connectionsQ.data}
          connectionsError={connectionsQ.isError}
          slack={saved.slack}
          channels={channels}
          onChange={(recipients, touched) => edit({ recipients }, touched && { section: 'recipient', ...touched })}
        />
        <NudgeEditor value={current.nudge} errors={errors.nudge} onChange={(nudge, field) => edit({ nudge }, field && { section: 'nudge', field })} />
        <section aria-labelledby="assessment-h" className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 id="assessment-h" className="text-[15px] font-semibold text-gray-900">Assessment</h2>
          <label className="mt-2 flex min-h-10 items-start gap-3 text-[13px]">
            <input
              type="checkbox"
              role="switch"
              aria-checked={current.assessment.auto_send}
              checked={current.assessment.auto_send}
              onChange={(e) => edit({ assessment: { auto_send: e.target.checked } })}
              className="mt-1 h-4 w-4 accent-navy"
            />
            <span>
              <span className="font-medium text-gray-800">Text the work-style assessment automatically</span>
              <span className="mt-0.5 block text-muted">Sent to new Service Advisor and General Manager applicants as soon as they apply. You can always send it by hand from a candidate's page.</span>
            </span>
          </label>
        </section>
      </div>
    </form>
  );
}
